import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { useCallback, useState } from 'react';
import type { HandlebarsBlockType } from '../../types';

export const HandlebarsBlockStartView: React.FC<NodeViewProps> = ({
  node,
  selected,
  editor,
  getPos,
  deleteNode,
}) => {
  const { blockType, variable, description, isValid, error, nonEditable } = node.attrs as {
    blockType: HandlebarsBlockType;
    variable: string;
    description: string | null;
    isValid: boolean;
    error: string | null;
    nonEditable: boolean;
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const tagClasses = [
    'handlebars-block-start',
    `block-${blockType}`,
    selected ? 'selected' : '',
    !isValid ? 'invalid' : '',
    description ? 'has-description' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const pos = getPos();
      if (typeof pos !== 'number') return;

      const blockId = node.attrs.blockId;

      // Find matching end tag - we'll preserve it and update it later
      let hasMatchingEnd = false;
      editor.state.doc.descendants((endNode) => {
        if (
          endNode.type.name === 'handlebarsBlockEnd' &&
          endNode.attrs.blockId === blockId
        ) {
          hasMatchingEnd = true;
          return false;
        }
      });

      // Store the blockId in editor storage so HandlebarsSuggestion knows we're editing
      // and should update the existing end tag instead of creating a new one
      if (hasMatchingEnd) {
        editor.storage.handlebarsBlockStart.editingEndBlockId = blockId;
      }

      // Delete only the start tag and trigger autocomplete with existing value
      editor
        .chain()
        .focus()
        .deleteRange({ from: pos, to: pos + node.nodeSize })
        .insertContent(`{{#${blockType} ${variable}`)
        .run();
    },
    [editor, getPos, node.nodeSize, node.attrs.blockId, blockType, variable]
  );

  const handleDeleteCancel = useCallback(() => {
    setShowDeleteConfirm(false);
    editor.commands.focus();
  }, [editor]);

  const handleDeleteBlockOnly = useCallback(() => {
    // Delete only the start and end tags, preserving content
    const pos = getPos();
    if (typeof pos !== 'number') return;

    const blockId = node.attrs.blockId;

    // Find and delete matching end tag first
    editor.state.doc.descendants((endNode, endPos) => {
      if (
        endNode.type.name === 'handlebarsBlockEnd' &&
        endNode.attrs.blockId === blockId
      ) {
        editor.chain().focus().deleteRange({
          from: endPos,
          to: endPos + endNode.nodeSize,
        }).run();
        return false;
      }
    });

    // Then delete this start tag
    deleteNode();
    setShowDeleteConfirm(false);
  }, [editor, getPos, node.attrs.blockId, deleteNode]);

  const handleDeleteAll = useCallback(() => {
    const pos = getPos();
    if (typeof pos !== 'number') return;

    const blockId = node.attrs.blockId;
    let endPos = -1;
    let endNodeSize = 0;

    // Find matching end tag position
    editor.state.doc.descendants((endNode, nodePos) => {
      if (
        endNode.type.name === 'handlebarsBlockEnd' &&
        endNode.attrs.blockId === blockId
      ) {
        endPos = nodePos;
        endNodeSize = endNode.nodeSize;
        return false;
      }
    });

    if (endPos > pos) {
      // Delete everything from start to end (inclusive)
      editor
        .chain()
        .focus()
        .deleteRange({ from: pos, to: endPos + endNodeSize })
        .run();
    } else {
      // No matching end found, just delete the start node
      deleteNode();
    }

    setShowDeleteConfirm(false);
  }, [editor, getPos, node.attrs.blockId, deleteNode]);

  return (
    <NodeViewWrapper as="span" className="handlebars-block-start-wrapper">
      <span
        className={tagClasses}
        title={variable ? `{{#${blockType} (${variable})}}` : `{{#${blockType}}}`}
      >
        <span className="block-indicator">#</span>
        {description ? (
          <span className="block-variable">{description}</span>
        ) : (
          <>
            <span className="block-type">{blockType}</span>
            {variable && <span className="block-variable">({variable})</span>}
          </>
        )}
        {error && (
          <span className="error-indicator" title={error}>
            !
          </span>
        )}
        {!nonEditable && (
          <button
            type="button"
            className="edit-button"
            onClick={handleEdit}
            title="Edit block"
            contentEditable={false}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
        )}
      </span>

      {showDeleteConfirm && (
        <span className="block-delete-confirmation" contentEditable={false}>
          <span className="message">Delete block?</span>
          <span className="actions">
            <button type="button" onClick={handleDeleteBlockOnly}>
              Keep content
            </button>
            <button type="button" className="danger" onClick={handleDeleteAll}>
              Delete all
            </button>
            <button type="button" onClick={handleDeleteCancel}>
              Cancel
            </button>
          </span>
        </span>
      )}
    </NodeViewWrapper>
  );
};

export default HandlebarsBlockStartView;
